<template>
  <div>
    <FormFieldsRow
      v-for="field in nestedFields"
      v-show="isFieldVisible(field)"
      :key="field.key"
      :index="field.key"
      :isArray="false"
      :isEditing="editable"
      class="mb-4 px-2 py-3"
    >
      <div>
        <label class="font-weight-bold text-mute">{{ field.label }}</label>
        <DynamicForm
          :editable="editable"
          :formId="`${field.key}NestedCreditForm`"
        />
      </div>
    </FormFieldsRow>
    <DynamicForm
      :editable="editable"
      formId="nonNestedCreditForm"
    />
  </div>
</template>

<script>
import { ref, computed, toRefs } from '@vue/composition-api'
import FormFieldsRow from '@/components/Forms/FormFieldsRow'
import { userHasAnyAllowedRole } from '@shared/permissions/validators'
import useDynamicForm from '@/components/DynamicForms/useDynamicForm'
import FormMixin from '@/components/Forms/FormMixin'

export default {
  mixins: [ FormMixin ],
  components: {
    DynamicForm: () => import('@/components/DynamicForms/DynamicForm'),
    FormFieldsRow },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    formValue: {
      type: Object,
      default: () => ({}),
    },
    editable: {
      type: Boolean,
      default: true,
    },
    iteration: {
      type: Number,
      default: 0,
    },
    nestedMode: {
      type: Boolean,
      default: true,
    },
  },
  setup (props, { root, emit }) {
    const { formValue, value } = toRefs(props)
    const user = root.$store.getters.user

    const fields = computed(() => Object.values(props.config.fields || {}))

    const nestedFields = fields.value.filter(field => field && field.fields)
    const nonNestedFields = fields.value.filter(field => field && !field.fields)
    const nonNestedQuestions = computed(() => nonNestedFields.map(it => ({ ...it, questionId: it.key })))
    const nestedQuestions = computed(() => nestedFields.map(it => ({ ...it, questionId: it.key })))

    const nonNestedTemplate = computed(() => nonNestedQuestions.value.map(q => q.questionId))
    const nestedTemplate = computed(() => nestedQuestions.value.map(q => q.questionId))

    function emitChanges () {
      if (!props.nestedMode) {
        emit('input', value.value)
      }
    }

    function isFieldVisible (field = {}) {
      return userHasAnyAllowedRole(user, null, field.rolesAllowedToSee) && !field.hidden
    }

    useDynamicForm({
      questions: nonNestedQuestions,
      template: nonNestedTemplate,
      formValue: props.nestedMode ? formValue : value,
      onUpdated: emitChanges,
      path: props.nestedMode ? 'credits' : undefined,
      formId: 'nonNestedCreditForm',
      disableActions: !props.nestedMode,
    })

    nestedTemplate.value.forEach((template, index) => {
      useDynamicForm({
        questions: ref(
          Object.values(nestedQuestions.value[index].fields).map(it => ({
            ...it,
            questionId: it.key,
          }))
        ),
        template: ref([
          {
            type: 'row',
            id: `${template}1`,
            children: [
              { type: 'question', id: 'min' },
              { type: 'question', id: 'max' },
            ],
          },
          {
            type: 'row',
            id: `${template}2`,
            children: [
              { type: 'question', id: 'value' },
              { type: 'question', id: 'operator' },
            ],
          },
        ]),
        formValue: props.nestedMode ? formValue : value,
        path: props.nestedMode ? `credits.${template}` : template,
        onUpdated: emitChanges,
        formId: `${template}NestedCreditForm`,
        disableActions: !props.nestedMode,
      })
    })

    return {
      nestedTemplate,
      nestedFields,
      isFieldVisible,
    }
  },
}
</script>
<style scoped>
.child-fields {
  flex-basis: 250px;
  min-width: 45%;
}
</style>
